import { CheckCircle2, AlertTriangle, CircleHelp } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AssessmentStatus } from '../ai-review/assessment';
import { ReviewDocument } from './reviewModel';
import { supabase } from '../../lib/supabase';

export type AiStatus = 'service_waiting' | 'human_approved' | 'pass' | 'issue' | 'review' | 'queued' | 'running' | 'manual_review' | 'failed' | 'none' | 'unavailable';
export function resolveAiStatuses(ids:string[], reviews:AssessmentStatus[], documents:ReviewDocument[]=[]) {
  const statuses:Record<string,AiStatus> = Object.fromEntries(ids.map(id=>[id,'none']));
  const allowed=['pass','issue','review','queued','running','manual_review','failed','none'];
  reviews.forEach(row=>{if(ids.includes(row.document_id)&&allowed.includes(row.ai_status))statuses[row.document_id]=row.reason==='service_waiting'?'service_waiting':row.ai_status as AiStatus;});
  documents.forEach(doc=>{if(doc.status==='approved')statuses[doc.document_id]='human_approved';});
  return statuses;
}
export function useAiReviewStatuses(documents:ReviewDocument[], revision:number) {
  const key=JSON.stringify(documents.map(({document_id,applicable_month,applicable_year,document_type,status})=>({document_id,applicable_month,applicable_year,document_type,status})).sort((a,b)=>a.document_id.localeCompare(b.document_id)));
  const [snapshot,setSnapshot]=useState<{key:string;values:Record<string,AiStatus>}>({key:'',values:{}});
  useEffect(()=>{
    let active=true;
    let inFlight=false;
    const snapshot:ReviewDocument[]=JSON.parse(key);
    const documentIds=snapshot.map(doc=>doc.document_id);
    async function load() {
      if(inFlight)return;
      inFlight=true;
      const values:Record<string,AiStatus>={};
      for(let offset=0;offset<documentIds.length;offset+=50){
        const batch=documentIds.slice(offset,offset+50);
        try {
          const client=supabase as SupabaseClient;
          const reviews=await client.from('document_review_status_current').select('*').in('document_id',batch);
          if(reviews.error)throw new Error('Unavailable');
          Object.assign(values,resolveAiStatuses(batch,(reviews.data||[]) as AssessmentStatus[],snapshot));
        } catch {batch.forEach(id=>{values[id]='unavailable';});}
        if(!active)return;
      }
      if(active)setSnapshot({key,values});
      inFlight=false;
    }
    void load();
    const timer=window.setInterval(()=>{if(!document.hidden)void load();},60000);
    return()=>{active=false;window.clearInterval(timer);};
  },[key,revision]);
  const values:Record<string,AiStatus>={...(snapshot.key===key?snapshot.values:{})};
  documents.forEach(doc=>{if(doc.status==='approved')values[doc.document_id]='human_approved';});
  return values;
}
const badges:Record<AiStatus,{label:string;title:string;color:string}>={
  service_waiting:{label:'Esperando servicio',title:'El servicio de análisis está temporalmente indisponible. Se reintentará automáticamente; el documento no fue rechazado.',color:'text-amber-800 bg-amber-50'},
  human_approved:{label:'Aprobado por revisor',title:'La aprobación humana prevalece sobre el estado del análisis automático.',color:'text-emerald-700 bg-emerald-50'},
  pass:{label:'Recomienda aprobar',title:'Recomendación de IA; requiere aprobación humana',color:'text-emerald-700 bg-emerald-50'},
  issue:{label:'Recomienda rechazar',title:'La IA detectó un incumplimiento de los criterios; decide el revisor',color:'text-red-800 bg-red-50'},
  review:{label:'Por resolver',title:'La IA no puede concluir o todavía falta una evaluación de criterios',color:'text-amber-800 bg-amber-50'},
  queued:{label:'En cola',title:'Pendiente de análisis por IA.',color:'text-slate-600 bg-slate-100'},
  running:{label:'Analizando',title:'La IA está leyendo el archivo.',color:'text-blue-700 bg-blue-50'},
  manual_review:{label:'Manual',title:'El archivo requiere revisión manual.',color:'text-amber-800 bg-amber-50'},
  failed:{label:'Análisis incompleto',title:'La IA no terminó de analizar el archivo. No es un rechazo del documento.',color:'text-amber-800 bg-amber-50'},
  none:{label:'—',title:'Sin análisis vigente.',color:'text-slate-500'},
  unavailable:{label:'—',title:'No se pudo consultar el estado de IA.',color:'text-slate-500'},
};
export default function AiReviewStatus({status}:{status?:AiStatus}) {
  const badge=status?badges[status]:{label:'…',title:'Consultando estado de IA.',color:'text-slate-500'};
  const Icon=(status==='pass'||status==='human_approved')?CheckCircle2:status==='issue'?AlertTriangle:status==='review'?CircleHelp:null;
  return <span title={badge.title} aria-label={`IA: ${badge.label}. ${badge.title}`} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium ${badge.color}`}>{Icon && <Icon size={13} aria-hidden="true"/>}{badge.label}</span>;
}
